import React, { useState } from 'react';
import { Eye, Loader2, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsDispatcher } from '@/hooks/useIsDispatcher';
import { formatUsPhone, useManualStopContact } from '@/hooks/useManualStopContacts';
import type { ContactAccessContext, ManualStopContact } from '@/types/manualStops';

interface CustomerContactRevealProps {
  contactId: string | null | undefined;
  /** Recorded in the PII access log — pass where the details are being shown. */
  context: ContactAccessContext;
}

const PREFERRED_LABEL = { call: 'Prefers a call', text: 'Prefers a text', email: 'Prefers email' };

const ContactDetails: React.FC<{ contact: ManualStopContact }> = ({ contact }) => {
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  return (
    <div className='space-y-0.5 text-xs'>
      <p className='font-medium text-foreground'>{name}</p>
      {contact.phone_e164 && (
        <a
          href={`tel:${contact.phone_e164}`}
          className='flex items-center gap-1 text-muted-foreground hover:text-foreground'
        >
          <Phone className='h-3 w-3' /> {formatUsPhone(contact.phone_e164)}
        </a>
      )}
      {contact.email && (
        <a
          href={`mailto:${contact.email}`}
          className='flex items-center gap-1 text-muted-foreground hover:text-foreground break-all'
        >
          <Mail className='h-3 w-3' /> {contact.email}
        </a>
      )}
      <p className='text-muted-foreground'>{PREFERRED_LABEL[contact.preferred_contact]}</p>
    </div>
  );
};

/**
 * Customer contact details for a customer manual stop, hidden until a dispatcher asks.
 * Each reveal is written to the access log, so nothing is fetched until the click.
 * Non-dispatchers see nothing (the database would refuse them anyway).
 */
export const CustomerContactReveal: React.FC<CustomerContactRevealProps> = ({
  contactId,
  context
}) => {
  const { isDispatcher } = useIsDispatcher();
  const [revealed, setRevealed] = useState(false);
  const query = useManualStopContact(contactId, context, revealed);

  if (!isDispatcher || !contactId) return null;

  if (!revealed) {
    return (
      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
        <span>Customer contact hidden</span>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-6 px-2 text-xs gap-1'
          onClick={() => setRevealed(true)}
        >
          <Eye className='h-3 w-3' /> Show
        </Button>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <p className='flex items-center gap-1 text-xs text-muted-foreground'>
        <Loader2 className='h-3 w-3 animate-spin' /> Loading contact…
      </p>
    );
  }
  if (query.isError) {
    return <p className='text-xs text-muted-foreground'>Couldn&apos;t load the contact.</p>;
  }
  const contact = query.data;
  if (!contact) {
    return <p className='text-xs text-muted-foreground'>Contact not available.</p>;
  }
  if (contact.purged) {
    return <p className='text-xs text-muted-foreground'>Customer details removed.</p>;
  }

  return <ContactDetails contact={contact} />;
};
